// src/pages/admin/RoleManagement.tsx

import React, { useState, useEffect } from 'react';
import {
  Plus, Edit, Trash2, Shield, RefreshCw, Users,
  ChevronDown, ChevronUp, Key, Search, AlertTriangle,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Modal, Input, Loading, LogoLoadingMark, LoadingLabel } from '../../components/ui';
import toast from 'react-hot-toast';
import { adminService } from '../../services/admin';
import api from '../../services/api';
import { Role } from '../../types';
import { Link } from 'react-router-dom';

// ========== TYPES ==========

interface RoleWithMeta extends Role {
  users_count?: number;
  capabilities_count?: number;
  capabilities?: string[];
}

// ========== CONSTANTS ==========

const HIERARCHY_CONFIG: Record<number, { label: string; color: string; bg: string; border: string; icon: string }> = {
  0: { label: 'Pimpinan Tertinggi', color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', icon: '👑' },
  1: { label: 'Pimpinan Universitas', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', icon: '🏛️' },
  2: { label: 'Pimpinan Fakultas', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', icon: '🏫' },
  3: { label: 'Pimpinan Unit', color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200', icon: '📋' },
  4: { label: 'Kepala Bagian', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: '📁' },
  5: { label: 'Kepala Sub-Unit', color: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-200', icon: '📂' },
  6: { label: 'Staff', color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', icon: '👤' },
};

const getHierarchy = (level: number) => {
  if (level <= 0) return HIERARCHY_CONFIG[0];
  if (level <= 1) return HIERARCHY_CONFIG[1];
  if (level <= 2) return HIERARCHY_CONFIG[2];
  if (level <= 3) return HIERARCHY_CONFIG[3];
  if (level <= 4) return HIERARCHY_CONFIG[4];
  if (level <= 5) return HIERARCHY_CONFIG[5];
  return HIERARCHY_CONFIG[6];
};

// ========== COMPONENT ==========

const RoleManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<RoleWithMeta[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editRole, setEditRole] = useState<RoleWithMeta | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    level: '3',
    dashboard_type: 'pelapor',
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [roleScopes, setRoleScopes] = useState<any[]>([]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await adminService.roles.getAll();
      let rolesData: any[] = [];

      if (Array.isArray(res)) {
        rolesData = res;
      } else if ((res as any)?.data && Array.isArray((res as any).data)) {
        rolesData = (res as any).data;
      }

      // Fetch capabilities count per role
      const enriched: RoleWithMeta[] = [];
      for (const role of rolesData) {
        let caps: string[] = [];
        try {
          const capRes = await api.get(`/admin/roles/${role.id}`);
          const capData = capRes.data?.data || capRes.data;
          caps = Array.isArray(capData?.capabilities) ? capData.capabilities : [];
        } catch {
          // Silently fail — capabilities endpoint might not exist for some roles
        }

        enriched.push({
          ...role,
          capabilities: caps,
          capabilities_count: caps.length,
        });
      }

      setRoles(enriched);

      // Fetch role scopes for deletion guard
      try {
        const scopeRes = await api.get('/admin/role-scopes');
        setRoleScopes(scopeRes.data?.data || scopeRes.data || []);
      } catch {
        // Fallback or ignore
      }
    } catch (e) {
      console.error('Error fetching roles:', e);
      toast.error('Gagal memuat data role');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditRole(null);
    setFormData({ name: '', display_name: '', description: '', level: '3', dashboard_type: 'pelapor' });
    setShowModal(true);
  };


  const openEdit = (role: RoleWithMeta) => {
    setEditRole(role);
    setFormData({
      name: role.name || '',
      display_name: role.display_name || '',
      description: role.description || '',
      level: String(role.level ?? 3),
      dashboard_type: (role as any).dashboard_type || 'pelapor',
    });

    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.display_name.trim()) {
      toast.error('Display name wajib diisi');
      return;
    }
    if (!editRole && !formData.name.trim()) {
      toast.error('Nama role wajib diisi');
      return;
    }

    // CHECK DUPLICATE NAME
    if (!editRole) {
      const normalizedName = formData.name.toLowerCase().replace(/\s+/g, '_');
      const isDuplicate = roles.some(r => r.name === normalizedName);
      if (isDuplicate) {
        toast.error(`Role dengan nama "${normalizedName}" sudah ada`);
        return;
      }
    }

    const levelNum = Number(formData.level);
    if (isNaN(levelNum) || levelNum < 0 || levelNum > 10) {
      toast.error('Level harus antara 0-10');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.toLowerCase().replace(/\s+/g, '_'),
        display_name: formData.display_name.trim(),
        description: formData.description.trim() || '',
        level: levelNum,
        dashboard_type: formData.dashboard_type,
      };


      if (editRole) {
        await adminService.roles.update(editRole.id, payload);
        toast.success('Role berhasil diperbarui');
      } else {
        await adminService.roles.create(payload);
        toast.success('Role berhasil ditambahkan');
      }

      setShowModal(false);
      fetchRoles();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Gagal menyimpan role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role: RoleWithMeta) => {
    if (role.name === 'admin') {
      toast.error('Role admin tidak dapat dihapus');
      return;
    }

    const userCount = role.users_count ?? 0;
    if (userCount > 0) {
      toast.error(`Role masih digunakan oleh ${userCount} user`);
      return;
    }

    // CHECK DEPENDENCIES
    const hasScopes = roleScopes?.some(s => s.role_id === role.id) || false;
    const hasCaps = (role.capabilities_count ?? 0) > 0;

    let warningMsg = `Yakin ingin menghapus role "${role.display_name}"?`;
    if (hasScopes) {
      warningMsg += '\n\n⚠️ Role ini memiliki Role Scopes yang akan ikut terhapus.';
    }
    if (hasCaps) {
      warningMsg += '\n\n⚠️ Role ini memiliki Capabilities yang akan ikut terhapus.';
    }

    if (!window.confirm(warningMsg)) {
      return;
    }

    setDeleting(role.id);
    try {
      await adminService.roles.delete(role.id);
      toast.success('Role berhasil dihapus');
      fetchRoles();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Gagal menghapus role');
    } finally {
      setDeleting(null);
    }
  };

  // Filter & Sort
  const filteredRoles = roles
    .filter((r) => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return (
        r.name.toLowerCase().includes(s) ||
        r.display_name.toLowerCase().includes(s) ||
        (r.description || '').toLowerCase().includes(s)
      );
    })
    .sort((a, b) => (a.level ?? 6) - (b.level ?? 6));

  // Stats
  const totalUsers = roles.reduce((sum, r) => sum + (r.users_count ?? 0), 0);
  const totalCaps = new Set(roles.flatMap((r) => r.capabilities || [])).size;
  const emptyRoles = roles.filter((r) => (r.users_count ?? 0) === 0).length;

  // Group by hierarchy
  const groupedRoles: Record<string, RoleWithMeta[]> = {};
  filteredRoles.forEach((role) => {
    const h = getHierarchy(role.level ?? 6);
    const key = h.label;
    if (!groupedRoles[key]) groupedRoles[key] = [];
    groupedRoles[key].push(role);
  });

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Admin', href: '/admin/settings' },
        { label: 'Role Management' },
      ]} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight flex items-center gap-2">
            <Shield className="w-7 h-7" />
            Role Management
          </h1>
          <p className="text-sm text-neutral-text-secondary mt-1">
            Kelola role dan hierarki organisasi — akses ditentukan oleh <Link to="/admin/capabilities" className="text-teal-main font-semibold hover:underline">Capabilities</Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchRoles}
            disabled={loading}
            icon={loading ? <LogoLoadingMark size="xs" /> : <RefreshCw className="w-4 h-4" />}
          >
            Refresh
          </Button>
          <Link to="/admin/capabilities">
            <Button variant="outline" size="sm" icon={<Key className="w-4 h-4" />}>
              Capabilities
            </Button>
          </Link>
          <Button variant="primary" size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>
            Tambah Role
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-teal-dark">{roles.length}</p>
              <p className="text-xs text-neutral-text-secondary">Total Role</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-teal-dark">{totalUsers}</p>
              <p className="text-xs text-neutral-text-secondary">Total User</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <Key className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-teal-dark">{totalCaps}</p>
              <p className="text-xs text-neutral-text-secondary">Unique Capabilities</p>
            </div>
          </div>
        </Card>

        <Card className="!p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-teal-dark">{emptyRoles}</p>
              <p className="text-xs text-neutral-text-secondary">Role Kosong</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-text-secondary" />
        <input
          type="text"
          placeholder="Cari role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-light"
        />
      </div>

      {/* Loading */}
      {loading ? (
        <Loading variant="inline" message="Memuat data role..." className="py-8" />
      ) : filteredRoles.length === 0 ? (
        <Card className="text-center py-16">
          <Shield className="w-12 h-12 mx-auto mb-3 text-neutral-text-secondary opacity-40" />
          <p className="text-lg font-bold">Tidak ada role ditemukan</p>
          {searchTerm && (
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => setSearchTerm('')}>
              Reset Pencarian
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedRoles).map(([groupLabel, groupRoles]) => {
            const firstRole = groupRoles[0];
            const hierarchy = getHierarchy(firstRole.level ?? 6);

            return (
              <div key={groupLabel}>
                {/* Group Header */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">{hierarchy.icon}</span>
                  <h2 className={`text-sm font-bold uppercase tracking-widest ${hierarchy.color}`}>
                    {groupLabel}
                  </h2>
                  <span className="text-xs text-neutral-text-secondary">
                    Level {firstRole.level ?? 6} • {groupRoles.length} role
                  </span>
                  <div className="flex-1 h-px bg-neutral-border ml-2" />
                </div>

                {/* Role Cards in Group */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupRoles.map((role) => {
                    const h = getHierarchy(role.level ?? 6);
                    const isExpanded = expandedId === role.id;
                    const isProtected = role.name === 'admin';
                    const userCount = role.users_count ?? 0;
                    const capsCount = role.capabilities_count ?? 0;

                    return (
                      <Card
                        key={role.id}
                        className={`relative overflow-hidden transition-all ${isExpanded ? 'ring-2 ring-teal-main/30 shadow-lg' : 'hover:shadow-md'
                          }`}
                      >
                        {/* Top accent */}
                        <div className={`absolute top-0 left-0 right-0 h-1 ${h.bg.replace('bg-', 'bg-').replace('-50', '-400')}`} />

                        {/* Header */}
                        <div className="flex items-start justify-between pt-2">
                          <div className="flex items-start gap-3">
                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${isProtected
                              ? 'bg-gradient-to-br from-rose-500 to-rose-600'
                              : 'bg-gradient-to-br from-teal-500 to-teal-600'
                              }`}>
                              <Shield className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-neutral-text-primary flex items-center gap-1.5">
                                {role.display_name}
                                {isProtected && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-600 font-bold uppercase">
                                    Protected
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-neutral-text-secondary font-mono">{role.name}</p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={() => setExpandedId(isExpanded ? null : role.id)}
                              className="p-1.5 rounded-lg hover:bg-neutral-bg transition-colors"
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="flex items-center gap-3 mt-3">
                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${userCount > 0 ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-neutral-bg border-neutral-border text-neutral-text-secondary'
                            }`}>
                            <Users className="w-3 h-3" />
                            <span className="text-xs font-bold">{userCount}</span>
                            <span className="text-[10px]">user</span>
                          </div>

                          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${capsCount > 0 ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-neutral-bg border-neutral-border text-neutral-text-secondary'
                            }`}>
                            <Key className="w-3 h-3" />
                            <span className="text-xs font-bold">{capsCount}</span>
                            <span className="text-[10px]">caps</span>
                          </div>

                          <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border ${h.bg} ${h.color} ${h.border}`}>
                            Lv.{role.level ?? 6}
                          </span>

                          <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${(role as any).dashboard_type === 'admin' ? 'bg-rose-100 text-rose-600' :
                            (role as any).dashboard_type === 'rektor' ? 'bg-amber-100 text-amber-600' :
                              (role as any).dashboard_type === 'sekretaris' ? 'bg-blue-100 text-blue-600' :
                                (role as any).dashboard_type === 'warek' ? 'bg-indigo-100 text-indigo-600' :
                                  (role as any).dashboard_type === 'dekan' ? 'bg-teal-100 text-teal-600' :
                                    (role as any).dashboard_type === 'observer' ? 'bg-gray-100 text-gray-600' :
                                      'bg-green-100 text-green-600'
                            }`}>
                            📊 {(role as any).dashboard_type || 'pelapor'}
                          </span>
                        </div>


                        {/* Description */}
                        {role.description && (
                          <p className="text-xs text-neutral-text-secondary mt-2 line-clamp-2">
                            {role.description}
                          </p>
                        )}

                        {/* Expanded Section */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-neutral-border space-y-3 animate-in">
                            {/* Capabilities Preview */}
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary mb-2">
                                Capabilities ({capsCount})
                              </p>
                              {capsCount > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {(role.capabilities || []).slice(0, 10).map((cap) => (
                                    <span
                                      key={cap}
                                      className="px-2 py-0.5 bg-purple-50 border border-purple-200 rounded text-[9px] font-medium text-purple-700"
                                    >
                                      {cap.replace(/_/g, ' ')}
                                    </span>
                                  ))}
                                  {capsCount > 10 && (
                                    <span className="px-2 py-0.5 bg-neutral-bg rounded text-[9px] font-medium text-neutral-text-secondary">
                                      +{capsCount - 10} lagi
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-neutral-text-secondary italic">
                                  Belum ada capabilities.{' '}
                                  <Link to="/admin/capabilities" className="text-teal-main font-semibold hover:underline">
                                    Kelola →
                                  </Link>
                                </p>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-2 pt-2">
                              <Button variant="outline" size="sm" onClick={() => openEdit(role)} icon={<Edit className="w-3.5 h-3.5" />}>
                                Edit
                              </Button>
                              <Link to="/admin/capabilities">
                                <Button variant="outline" size="sm" icon={<Key className="w-3.5 h-3.5" />}>
                                  Capabilities
                                </Button>
                              </Link>
                              {!isProtected && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleDelete(role)}
                                  disabled={deleting === role.id || userCount > 0}
                                  icon={deleting === role.id ? <LogoLoadingMark size="xs" /> : <Trash2 className="w-3.5 h-3.5" />}
                                >
                                  Hapus
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => !saving && setShowModal(false)}
        title={editRole ? 'Edit Role' : 'Tambah Role Baru'}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
              Batal
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving}
              icon={saving ? undefined : editRole ? <Edit className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            >
              {saving
                ? <LoadingLabel label={editRole ? 'Menyimpan...' : 'Menambahkan...'} />
                : editRole ? 'Simpan' : 'Tambah Role'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-xs text-blue-700">
              <strong>Level</strong> menentukan posisi dalam hierarki organisasi universitas.
              <br />
              • <strong>0</strong>: Rektor / Admin
              • <strong>1</strong>: Warek / Sekretaris / Senat
              • <strong>2</strong>: Dekan / Ketua Lembaga
              <br />
              • <strong>3</strong>: Kaprodi / Ka Pusat
              • <strong>4</strong>: Kabag
              • <strong>5</strong>: Ka TU / Kalab
              • <strong>6+</strong>: Staff
              <br /><br />
              <em>Akses fitur ditentukan oleh <strong>Capabilities</strong>, bukan level.</em>
            </p>
          </div>

          <Input
            label="Nama Role (Kode)"
            placeholder="contoh: kepala_lab"
            value={formData.name}
            onChange={(e) => setFormData({
              ...formData,
              name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')
            })}
            helpText="Huruf kecil, angka, dan underscore saja"
            required
            disabled={!!editRole}
          />

          <Input
            label="Display Name"
            placeholder="contoh: Kepala Laboratorium"
            value={formData.display_name}
            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            helpText="Nama yang ditampilkan di sistem"
            required
          />

          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
              Level Hierarki
            </label>

            {/* Visual Level Selector */}
            <div className="grid grid-cols-4 gap-2 mb-2">
              {[0, 1, 2, 3, 4, 5, 6].map((lv) => {
                const h = getHierarchy(lv);
                const isSelected = Number(formData.level) === lv;
                return (
                  <button
                    key={lv}
                    type="button"
                    onClick={() => setFormData({ ...formData, level: String(lv) })}
                    className={`p-2 rounded-lg border-2 text-center transition-all ${isSelected
                      ? `${h.bg} ${h.border} ${h.color} shadow-sm ring-1 ring-offset-1`
                      : 'border-neutral-border hover:border-neutral-text-secondary'
                      }`}
                  >
                    <span className="text-lg">{h.icon}</span>
                    <p className="text-[9px] font-bold mt-0.5">{lv}</p>
                    <p className="text-[8px] text-neutral-text-secondary leading-tight">{h.label}</p>
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-neutral-text-secondary">
              Terpilih: <strong>{getHierarchy(Number(formData.level)).label}</strong> (Level {formData.level})
            </p>
          </div>

          {/* Dashboard Type Selector */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-2">
              Tipe Dashboard
            </label>
            <p className="text-xs text-neutral-text-secondary mb-3">
              Pilih tampilan dashboard yang akan dilihat pengguna dengan role ini.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { value: 'admin', label: 'Administrator', icon: '⚙️', desc: 'Kelola sistem', color: 'border-rose-300 bg-rose-50' },
                { value: 'rektor', label: 'Rektor', icon: '👑', desc: 'Eksekutif penuh', color: 'border-amber-300 bg-amber-50' },
                { value: 'sekretaris', label: 'Sekretaris', icon: '📋', desc: 'Kelola RAPIM', color: 'border-blue-300 bg-blue-50' },
                { value: 'warek', label: 'Wakil Rektor', icon: '🎓', desc: 'Validasi & monitoring', color: 'border-indigo-300 bg-indigo-50' },
                { value: 'dekan', label: 'Dekan/Ka. Lembaga', icon: '🏛️', desc: 'Validasi unit', color: 'border-teal-300 bg-teal-50' },
                { value: 'observer', label: 'Pengamat', icon: '👁️', desc: 'Read-only semua', color: 'border-gray-300 bg-gray-50' },
                { value: 'pelapor', label: 'Pelapor', icon: '📝', desc: 'Buat & pantau laporan', color: 'border-green-300 bg-green-50' },
              ].map((opt) => {
                const isSelected = formData.dashboard_type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, dashboard_type: opt.value as any })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${isSelected
                      ? `${opt.color} shadow-md ring-2 ring-offset-1 ring-teal-main/30`
                      : 'border-neutral-border hover:border-neutral-text-secondary'
                      }`}
                  >
                    <span className="text-xl block mb-1">{opt.icon}</span>
                    <p className="text-xs font-bold">{opt.label}</p>
                    <p className="text-[9px] text-neutral-text-secondary">{opt.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>


          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary mb-1.5">
              Deskripsi
            </label>
            <textarea
              className="w-full px-3 py-2 border border-neutral-border rounded-lg text-sm resize-none focus:ring-2 focus:ring-teal-main/20 focus:border-teal-main transition-colors"
              rows={3}
              placeholder="Deskripsi singkat tentang role ini..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RoleManagement;
